import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import ResizeObserver from 'resize-observer-polyfill';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'sigv-text-collapse',
  templateUrl: './text-collapse.component.html',
  styleUrls: ['./text-collapse.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextCollapseComponent implements OnDestroy, AfterViewInit {
  @ViewChild('measuredDiv') measuredDiv?: ElementRef<HTMLDivElement>;
  @ViewChild('measuredMoreDev') measuredMoreDev?: ElementRef<HTMLDivElement>;
  @ViewChild('textCollapse') textCollapse?: ElementRef<HTMLDivElement>;

  private readonly destroy$ = new Subject();
  private readonly updateDom$ = new Subject();

  /**
   * 顯示的內文
   */
  @Input() set content(value: string) {
    if (typeof value === 'string') {
      const replaceRegexp = new RegExp(this.breakBy, 'g');

      this.innerText = value;
      this.customBreakLineText = value.replace(
        replaceRegexp,
        `${this.breakBy}\n`
      );

      if (this.isUpdateDomeInit) {
        this.updateDom();
      }
    }
  }

  /**
   * @description 決定展開時如遇自訂字串時(預設是', ')是否會強制換行
   */
  @Input() customBreakLine = false;

  /**
   * @description 自訂斷行的字串
   */
  @Input() breakBy = ', ';

  /**
   * 文字收合時顯示的行數，預設值為 2
   */
  @Input() line = 2;

  customBreakLineText = '';
  innerText = '';
  linedContent: string[] = [];
  resizeObserver: ResizeObserver;

  // status
  autoScale = false;
  isCollapsed = true;
  isContentOverflow = false;
  isUpdateDomeInit = false;
  resizeCount = 0;

  constructor(
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef
  ) {
    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateDom();
      });

    // 組件大小發生變化時重新計算內容
    this.resizeObserver = new ResizeObserver(() => {
      this.resizeCount++;

      // 前2次 Resize 為組件初始化導致大小發生改變，不重新計算
      if (this.isUpdateDomeInit && this.resizeCount > 2) {
        this.updateDom();
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.textCollapse) {
      this.resizeObserver.observe(this.textCollapse.nativeElement);
    }

    this.updateDom$
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => {
        this.calculateContent();
      });

    this.updateDom(true);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.textCollapse) {
      this.resizeObserver.unobserve(this.textCollapse.nativeElement);
    }
  }

  private updateDom(isInit?: boolean): void {
    if (isInit) {
      this.isUpdateDomeInit = true;
    }

    if (this.isCollapsed && this.isUpdateDomeInit) {
      this.updateDom$.next();
    }
  }

  setIsCollapsed(value: boolean): void {
    this.isCollapsed = value;
  }

  /**
   * @description 計算每行文字及斷行處理
   */
  private calculateContent(): void {
    if (!this.innerText) {
      this.linedContent = [];
      this.isContentOverflow = false;
      return;
    }

    const totalLinedContent = this.getTotalLinedContent();
    const newLinedContent = totalLinedContent.slice(0, this.line);

    this.isContentOverflow = totalLinedContent.length > this.line;

    if (this.isContentOverflow) {
      this.setOverflowText(newLinedContent);
    }

    // 需等 setOverflowText 呼叫完再賦值
    this.linedContent = newLinedContent;

    this.cdr.markForCheck();
  }

  /**
   * @description 檢查含有 ....more 的句子是否過寬，如過寬則移除多餘的文字。
   */
  private setOverflowText(list: string[]): void {
    if (
      !this.measuredMoreDev?.nativeElement ||
      !this.textCollapse?.nativeElement
    ) {
      return;
    }

    const translatedMoreText = `...${this.translateService.instant(
      'SigvLibrary.More'
    )}`;

    const { clientWidth } = this.textCollapse?.nativeElement;
    const text = [...list[this.line - 1].split('')];
    const textLastIndex = text.length - 1;
    const content = text.slice(0, textLastIndex);

    // 判斷字串是否過長，是的話則移除最後一個字元
    for (let i = textLastIndex; i >= 0; i--) {
      this.measuredMoreDev!.nativeElement.innerText = `${content.join(
        ''
      )} ${translatedMoreText}`;

      const { scrollWidth } = this.measuredMoreDev?.nativeElement;

      if (scrollWidth < clientWidth) {
        break;
      }

      // 移除最後一個字元
      content.pop();
    }

    // 重新設定 ...More 句子文字
    list[this.line - 1] = content.join('');
  }

  /**
   * @description 取得 innerText 渲染到畫面後每行的字串
   */
  private getTotalLinedContent(): string[] {
    const textNode = this.measuredDiv?.nativeElement.firstChild;
    const textContent = this.measuredDiv?.nativeElement?.textContent;
    const range = document.createRange();
    const lines = [];
    let lineCharacters: string[] = [];

    if (!textNode || !textContent) {
      return [];
    }

    // 使用 createRange 計算 innerText 渲染到畫面後每行的字傳
    for (let i = 0; i < textContent.length; i++) {
      range.setStart(textNode, 0);
      range.setEnd(textNode, i + 1);

      // 每行的 Index
      const lineIndex = range.getClientRects().length - 1;

      if (!lines[lineIndex]) {
        lineCharacters = [];
        // 新增一行
        lines.push(lineCharacters);
      }

      // Push 新子元到同一行文字
      lineCharacters.push(textContent.charAt(i));
    }

    // 將字串陣列轉為字串，並移除空字串的行數
    const result = lines
      .map((value: string[]) => value.join('').trim())
      .filter((value) => !!value);

    return result;
  }
}
